import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { Download, Loader2, Undo2, Upload } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  client,
  exportOasisBatch,
  exportOasisVisit,
  listOasisManagerVisits,
  markVisitsExported,
  unmarkVisitsExported,
} from '../services/api';
import type { OasisManagerVisitItem } from '../services/api/types.gen';
import type { OASISManagerFilters } from '../store/slices/oasisManagerSlice';
import { formatDateToMMDDYYYY } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToastContext';
import { OASIS_VISIT_TYPE_LABELS } from '../constants/oasisVisitTypes';

interface ParsedError {
  message: string;
  error_code?: string;
}

async function parseErrorDetail(error: unknown): Promise<ParsedError | null> {
  if (error instanceof Blob) {
    try {
      const text = await error.text();
      const json = JSON.parse(text);
      const detail = json.detail;
      if (typeof detail === 'object' && detail !== null) {
        return detail as ParsedError;
      }
      if (typeof detail === 'string') {
        return { message: detail };
      }
      return null;
    } catch {
      return null;
    }
  }
  if (typeof error === 'object' && error !== null) {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === 'object' && detail !== null) {
      return detail as ParsedError;
    }
    if (typeof detail === 'string') {
      return { message: detail };
    }
  }
  return null;
}

const STATUS_BY_TAB: Record<OASISManagerFilters['activeTab'], string> = {
  ready_to_export: 'completed',
  exported: 'exported',
};

const OASISManager: React.FC = () => {
  const { oasisManagerFilters, setOASISManagerFilters } = useStore(
    useShallow(state => ({
      oasisManagerFilters: state.oasisManagerFilters,
      setOASISManagerFilters: state.setOASISManagerFilters,
    }))
  );

  const [visits, setVisits] = useState<OasisManagerVisitItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [clinicConfigError, setClinicConfigError] = useState<string | null>(
    null
  );
  const [searchInput, setSearchInput] = useState(
    oasisManagerFilters.patientSearch
  );
  const toast = useToast();
  const navigate = useNavigate();

  // Debounce patient search — wait 350ms after typing stops before fetching
  useEffect(() => {
    const timer = setTimeout(() => {
      setOASISManagerFilters({ patientSearch: searchInput });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const pageSize = 50;

  const fetchVisits = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = STATUS_BY_TAB[oasisManagerFilters.activeTab];
      const response = await listOasisManagerVisits({
        client,
        query: {
          status: [status],
          patient_search: oasisManagerFilters.patientSearch || undefined,
          clinic_id: oasisManagerFilters.clinicId || undefined,
          page,
          page_size: pageSize,
        },
      });
      if (response.data) {
        setVisits(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to load OASIS visits:', err);
      toast.error('Failed to load OASIS visits. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [
    oasisManagerFilters.activeTab,
    oasisManagerFilters.patientSearch,
    oasisManagerFilters.clinicId,
    page,
  ]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  useEffect(() => {
    const visibleIds = new Set(visits.map(visit => visit.visit_id));
    setSelectedIds(prev => {
      const next = new Set(
        Array.from(prev).filter(visitId => visibleIds.has(visitId))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [visits]);

  // Reset selection and page when tab changes
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
  }, [oasisManagerFilters.activeTab]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === visits.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visits.map(v => v.visit_id)));
    }
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) return;
    setIsExporting(true);
    setClinicConfigError(null);
    try {
      const ids = Array.from(selectedIds);

      if (ids.length === 1) {
        // Single visit — export as XML
        const response = await exportOasisVisit({
          client,
          path: { visit_id: ids[0] },
          body: { answers: {} },
        });
        if (response.error) {
          const detail = await parseErrorDetail(response.error);
          if (detail && detail.error_code === 'CLINIC_CONFIG_REQUIRED') {
            setClinicConfigError(detail.message);
          } else {
            toast.error('Failed to export OASIS XML.');
          }
          return;
        }
        if (response.data) {
          const blob = new Blob([response.data as BlobPart], {
            type: 'application/xml',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `oasis_${ids[0]}.xml`;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          toast.success('Exported OASIS assessment.');
        }
      } else {
        // Multiple visits — export as ZIP
        const response = await exportOasisBatch({
          client,
          body: {
            visits: ids.map(id => ({
              visit_id: id,
              trans_type_cd: '1',
              correction_num: '00',
            })),
          },
        });
        if (response.error) {
          const detail = await parseErrorDetail(response.error);
          if (detail && detail.error_code === 'CLINIC_CONFIG_REQUIRED') {
            setClinicConfigError(detail.message);
          } else {
            toast.error('Failed to export OASIS ZIP.');
          }
          return;
        }
        if (response.data) {
          const blob = new Blob([response.data as BlobPart], {
            type: 'application/zip',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `oasis_batch_${new Date().toISOString().slice(0, 10)}.zip`;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          toast.success(`Exported ${ids.length} OASIS assessments.`);
        }
      }
    } catch (err) {
      // Network or unexpected errors — API errors are handled via response.error above
      console.error('Error exporting OASIS:', err);
      toast.error('Failed to export OASIS. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleMarkExported = async () => {
    if (selectedIds.size === 0) return;
    setIsMarking(true);
    try {
      const response = await markVisitsExported({
        client,
        body: {
          visit_ids: Array.from(selectedIds),
        },
      });
      if (response.error) {
        const detail = (response.error as { detail?: string })?.detail;
        toast.error(detail ?? 'Failed to mark visits as exported.');
        return;
      }
      if (response.data) {
        const { updated_count, skipped_ids } = response.data;
        if (skipped_ids.length > 0) {
          toast.success(
            `Marked ${updated_count} visit(s) as exported. ${skipped_ids.length} skipped.`
          );
        } else {
          toast.success(`Marked ${updated_count} visit(s) as exported.`);
        }
        setSelectedIds(new Set());
        fetchVisits();
      }
    } catch (err) {
      console.error('Error marking visits as exported:', err);
      toast.error('Failed to mark visits as exported. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const handleUnmarkExported = async () => {
    if (selectedIds.size === 0) return;
    setIsMarking(true);
    try {
      const response = await unmarkVisitsExported({
        client,
        body: { visit_ids: Array.from(selectedIds) },
      });
      if (response.error) {
        toast.error('Failed to move visits back to ready.');
        return;
      }
      toast.success('Moved visit(s) back to Ready to Export.');
      setSelectedIds(new Set());
      fetchVisits();
    } catch (err) {
      console.error('Error unmarking visits:', err);
      toast.error('Failed to move visits back to ready. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className='flex h-full flex-col'>
      <div className='border-b px-6 py-4'>
        <h1 className='text-lg font-semibold'>OASIS Manager</h1>
        <p className='text-sm text-muted-foreground'>
          Export OASIS assessments and track uploads to iQIES
        </p>
      </div>

      <div className='flex-1 overflow-auto px-6 py-4'>
        <Tabs
          value={oasisManagerFilters.activeTab}
          onValueChange={v =>
            setOASISManagerFilters({
              activeTab: v as OASISManagerFilters['activeTab'],
            })
          }
        >
          <div className='flex items-center justify-between gap-4 mb-4'>
            <TabsList>
              <TabsTrigger value='ready_to_export'>Ready to Export</TabsTrigger>
              <TabsTrigger value='exported'>Exported</TabsTrigger>
            </TabsList>

            <div className='flex items-center gap-2'>
              <Input
                placeholder='Search patients...'
                inputSize='sm'
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className='w-56'
              />
            </div>
          </div>

          {/* Clinic configuration error */}
          {clinicConfigError && (
            <div className='flex items-center gap-3 mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md'>
              <div className='flex-1 text-sm'>
                <p className='font-medium text-destructive'>
                  Clinic configuration required
                </p>
                <p className='text-muted-foreground mt-1'>
                  {clinicConfigError}. Update the clinic settings in{' '}
                  <button
                    className='underline text-primary hover:text-primary/80'
                    onClick={() => navigate('/admin')}
                  >
                    Admin &rarr; Clinics
                  </button>
                  .
                </p>
              </div>
              <Button
                size='sm'
                variant='ghost'
                onClick={() => setClinicConfigError(null)}
              >
                Dismiss
              </Button>
            </div>
          )}

          {/* Action bar */}
          {selectedIds.size > 0 && (
            <div className='flex items-center gap-3 mb-4 p-3 bg-muted rounded-md'>
              <span className='text-sm font-medium'>
                {selectedIds.size} selected
              </span>
              <Button size='sm' onClick={handleExport} disabled={isExporting}>
                {isExporting ? (
                  <Loader2 className='h-4 w-4 animate-spin mr-1' />
                ) : (
                  <Download className='h-4 w-4 mr-1' />
                )}
                {selectedIds.size === 1 ? 'Export XML' : 'Export ZIP'}
              </Button>
              {oasisManagerFilters.activeTab === 'ready_to_export' && (
                <Button
                  size='sm'
                  variant='outline'
                  onClick={handleMarkExported}
                  disabled={isMarking}
                >
                  {isMarking ? (
                    <Loader2 className='h-4 w-4 animate-spin mr-1' />
                  ) : (
                    <Upload className='h-4 w-4 mr-1' />
                  )}
                  Mark as Uploaded
                </Button>
              )}
              {oasisManagerFilters.activeTab === 'exported' && (
                <Button
                  size='sm'
                  variant='outline'
                  onClick={handleUnmarkExported}
                  disabled={isMarking}
                >
                  {isMarking ? (
                    <Loader2 className='h-4 w-4 animate-spin mr-1' />
                  ) : (
                    <Undo2 className='h-4 w-4 mr-1' />
                  )}
                  Move to Ready
                </Button>
              )}
            </div>
          )}

          <TabsContent value={oasisManagerFilters.activeTab} className='mt-0'>
            {isLoading ? (
              <div className='flex items-center justify-center py-12'>
                <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              </div>
            ) : visits.length === 0 ? (
              <div className='flex items-center justify-center py-12 text-sm text-muted-foreground'>
                {oasisManagerFilters.activeTab === 'ready_to_export'
                  ? 'No completed OASIS visits ready to export.'
                  : 'No exported OASIS visits.'}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-10'>
                        <Checkbox
                          checked={
                            visits.length > 0 &&
                            selectedIds.size === visits.length
                          }
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Visit Type</TableHead>
                      <TableHead>Visit Date</TableHead>
                      <TableHead>Clinician</TableHead>
                      <TableHead>Clinic</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visits.map(visit => (
                      <TableRow key={visit.visit_id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(visit.visit_id)}
                            onCheckedChange={() => toggleSelect(visit.visit_id)}
                          />
                        </TableCell>
                        <TableCell className='font-medium'>
                          {visit.patient_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant='outline' className='text-xs'>
                            {OASIS_VISIT_TYPE_LABELS[visit.visit_type] ??
                              visit.visit_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatDateToMMDDYYYY(visit.visit_date)}
                        </TableCell>
                        <TableCell>
                          {visit.assigned_clinician_name ?? '—'}
                        </TableCell>
                        <TableCell>{visit.clinic_name ?? '—'}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              visit.status === 'exported'
                                ? 'default'
                                : 'secondary'
                            }
                            className='text-xs'
                          >
                            {visit.status === 'exported'
                              ? 'Exported'
                              : 'Completed'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className='flex items-center justify-between mt-4'>
                    <span className='text-sm text-muted-foreground'>
                      Showing {(page - 1) * pageSize + 1}–
                      {Math.min(page * pageSize, total)} of {total}
                    </span>
                    <div className='flex gap-2'>
                      <Button
                        size='sm'
                        variant='outline'
                        disabled={page <= 1}
                        onClick={() => setPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        size='sm'
                        variant='outline'
                        disabled={page >= totalPages}
                        onClick={() => setPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default OASISManager;
